import * as React from "react";

import { ViewportSyncMode } from "@/shared/state/visualization/schema";

import type { SeriesAlignment } from "../alignment";
import { ZoomMode } from "../messaging/commands";
import { PlotTool } from "../plotTools/PlotTool";

export interface Region {
  width: number;
  height: number;
  top: number;
  left: number;
}

interface PlotPanelContext {
  activeTool: PlotTool;
  setActiveTool: (tool: PlotTool) => void;

  /**
   * Where each series plots in aligned mode. `undefined` when the panel
   * plots absolute time, and while an event-anchored alignment's events
   * load. The panel resolves it once and shares it here, so panel code
   * reads this rather than calling `useSeriesAlignment` again.
   */
  alignment: SeriesAlignment | undefined;

  /**
   * Whether the panel's config asks for x-axis alignment.
   *
   * True as soon as that setting is applied, including while an
   * event-anchored alignment's events load and `alignment` is still
   * `undefined`. Anything that would otherwise flip twice across that load
   * reads this instead of `alignment !== undefined`: the controls it
   * disables, and the viewport sync it suspends.
   */
  isAligned: boolean;

  viewportSyncMode: ViewportSyncMode;
  setViewportSyncMode: (next: ViewportSyncMode) => void;

  isLegendShown: boolean;
  setIsLegendShown: (next: boolean) => void;

  selectedRegion: Region | null;
  setSelectedRegion: (next: Region | null) => void;

  zoomMode: ZoomMode;
  setZoomMode: (next: ZoomMode) => void;
}

export const PlotPanelContext = React.createContext<PlotPanelContext>({
  activeTool: PlotTool.Move,
  setActiveTool: () => {},

  alignment: undefined,
  isAligned: false,

  viewportSyncMode: "x",
  setViewportSyncMode: () => {},

  isLegendShown: true,
  setIsLegendShown: () => {},

  selectedRegion: null,
  setSelectedRegion: () => {},

  zoomMode: "x",
  setZoomMode: () => {},
});
